/**
 * Every date boundary in this system is IST — GBV-DEV-2026-001 Section 16.
 * Store UTC, display IST, and never use raw `new Date()` boundaries for "today".
 *
 * IST is UTC+05:30 and has never observed daylight saving, so a fixed offset is
 * exactly correct here and avoids depending on the host's tz database.
 */
export const IST_OFFSET_MINUTES = 330;
const IST_OFFSET_MS = IST_OFFSET_MINUTES * 60 * 1000;
/** The wall-clock IST instant, expressed as a Date whose UTC parts read as IST. */
function toIstParts(at = new Date()) {
    return new Date(at.getTime() + IST_OFFSET_MS);
}
/** Today in IST as 'YYYY-MM-DD'. Use this for every daily counter key. */
export function istToday(at = new Date()) {
    return istDateString(at);
}
export function istDateString(at = new Date()) {
    const p = toIstParts(at);
    const y = p.getUTCFullYear();
    const m = String(p.getUTCMonth() + 1).padStart(2, '0');
    const d = String(p.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}
/** UTC instant of IST midnight that begins the given IST calendar day. */
export function istDayStart(dateString) {
    const [y, m, d] = dateString.split('-').map(Number);
    if (!y || !m || !d)
        throw new RangeError(`Expected YYYY-MM-DD, received "${dateString}".`);
    return new Date(Date.UTC(y, m - 1, d) - IST_OFFSET_MS);
}
/** UTC instant at which the given IST calendar day ends (exclusive). */
export function istDayEnd(dateString) {
    const start = istDayStart(dateString);
    return new Date(start.getTime() + 24 * 60 * 60 * 1000);
}
/** Half-open [start, end) range covering one IST day — what every daily query needs. */
export function istDayRange(dateString = istToday()) {
    return { start: istDayStart(dateString), end: istDayEnd(dateString) };
}
/** Unix seconds at the next IST midnight. Redis usage counters expire here. */
export function istMidnightEpoch(at = new Date()) {
    const tomorrow = istDayEnd(istToday(at));
    return Math.floor(tomorrow.getTime() / 1000);
}
/** IST calendar day n days before today, as 'YYYY-MM-DD'. */
export function istDaysAgo(days, at = new Date()) {
    return istDateString(new Date(at.getTime() - days * 24 * 60 * 60 * 1000));
}
/** Is this UTC instant inside today's IST day? */
export function isIstToday(when, at = new Date()) {
    return istDateString(when) === istDateString(at);
}
/** Formats a UTC instant as IST wall-clock time, e.g. "6:31 PM". */
export function formatIstTime(when) {
    const p = toIstParts(when);
    let hours = p.getUTCHours();
    const minutes = String(p.getUTCMinutes()).padStart(2, '0');
    const suffix = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    return `${hours}:${minutes} ${suffix}`;
}
/** Formats a UTC instant as an IST date, e.g. "3 Aug 2026". */
export function formatIstDate(when) {
    const p = toIstParts(when);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${p.getUTCDate()} ${months[p.getUTCMonth()]} ${p.getUTCFullYear()}`;
}
export function formatIstDateTime(when) {
    return `${formatIstDate(when)}, ${formatIstTime(when)}`;
}
/**
 * Relative time for timeline entries and "last contact" columns.
 * Deliberately coarse — an agent needs "2 days ago", not "1 day 22 hours".
 */
export function relativeTime(when, at = new Date()) {
    const diffMs = at.getTime() - when.getTime();
    const future = diffMs < 0;
    const abs = Math.abs(diffMs);
    const minute = 60_000;
    const hour = 60 * minute;
    const day = 24 * hour;
    if (abs < minute)
        return future ? 'in a moment' : 'just now';
    if (abs < hour) {
        const n = Math.floor(abs / minute);
        return future ? `in ${n}m` : `${n}m ago`;
    }
    if (abs < day) {
        const n = Math.floor(abs / hour);
        return future ? `in ${n}h` : `${n}h ago`;
    }
    if (abs < 7 * day) {
        const n = Math.floor(abs / day);
        return future ? `in ${n}d` : `${n}d ago`;
    }
    return formatIstDate(when);
}
/** True when a follow-up is due today in IST or already past. */
export function isOverdue(nextFollowUpAt, at = new Date()) {
    if (!nextFollowUpAt)
        return false;
    return istDateString(nextFollowUpAt) <= istDateString(at);
}
/** Parses "18:30" against an IST day, returning the UTC instant. */
export function istTimeOnDay(dateString, hhmm) {
    const [h, m] = hhmm.split(':').map(Number);
    if (h === undefined || m === undefined) {
        throw new RangeError(`Expected HH:MM, received "${hhmm}".`);
    }
    return new Date(istDayStart(dateString).getTime() + (h * 60 + m) * 60 * 1000);
}
//# sourceMappingURL=date.js.map